import React, { useCallback } from "react";

import Above from "core-web/components/Breakpoints/Above";
import ArrowIcon from "theme/components/icons/ArrowIcon";
import GalleryCard from "./GalleryCard";
import { PLACEHOLDER } from "theme/config/constants";
import PropTypes from "prop-types";
import TouchableSlider from "core-web/components/sliders/TouchableSlider";
import { above } from "core-web/utils/mediaqueries";
import showOverlay from "core-web/state/models/Overlay/dispatches/showOverlay";
import styled from "theme/libs/styled";
import zoomInSvg from "theme/assets/icons/zoomIn";
import { useTranslation } from "react-i18next";

const MediaWrapper = styled("div")`
    width: 100%;
    position: relative;
    background-color: ${({ theme }) => theme.colors.neutral_50};
    max-width: 100vw;
    max-height: 80vh;
    border-radius: 8px;
    overflow: hidden;

    ${({ theme }) => theme.below.tabletLg} {
        aspect-ratio: 3 / 4;
    }

    &:after {
        content: "";
        display: block;
        padding-bottom: 100%;
    }

    & > div {
        position: absolute;
        inset: 0;
        & > div,
        & > div > div {
            height: 100%;
        }
    }

    ${above.tabletSm} {
        &.zoomable .cursor {
            cursor: pointer;
            cursor: ${`url(${zoomInSvg}) 12 12, pointer`};
        }
    }

    ${({ theme }) => theme.above.tabletLg} {
        border-radius: 12px;
    }
`;

const NavigationButton = styled("button")`
    display: none;
    position: absolute;
    top: 50%;
    align-items: center;
    justify-content: center;
    transform: translateY(-50%);
    background-color: ${({ theme }) => theme.colors.white};
    z-index: 1;
    width: 40px;
    height: 40px;
    border-radius: 40px;
    margin: 0 16px;
    transition: all ${({ theme }) => theme.transitions.primary};

    ${({ theme }) => theme.above.mobileLg} {
        display: flex;
    }

    ${({ theme }) => theme.media.hover} {
        display: flex;

        &:hover {
            background-color: ${({ theme }) => theme.colors.black};
            color: ${({ theme }) => theme.colors.white};
        }
    }
`;

const Slide = styled("div")`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 65px;
    border: none;

    ${({ theme }) => theme.above.tabletLg} {
        border-radius: 8px;
        overflow: hidden;
    }

    ${above.sm} {
        padding: 126px;
    }

    ${above.lg} {
        padding: 0;
    }
`;

const MediaGallery = ({ image, medias, sliderProps, sliderOptions }) => {
    const { t } = useTranslation();

    const { activeIndex, slidePrev, slideNext } = sliderOptions;
    const isSlider = medias.length > 1;

    const onImageClick = useCallback(() => {
        showOverlay("gallery_zoom_overlay", { media: medias, startIndex: activeIndex, zIndex: 10002 });
    }, [activeIndex, medias]);

    const isImage = !!medias[activeIndex]?.image;

    return isSlider ? (
        <>
            <MediaWrapper className="zoomable">
                <Above
                    breakpoint="tabletSm"
                    render={() => (
                        <>
                            <NavigationButton aria-label={t("aria_labels.previous_image")} left="0" onClick={slidePrev}>
                                <ArrowIcon direction="left" width="16px" height="14px" />
                            </NavigationButton>
                            <NavigationButton aria-label={t("aria_labels.next_image")} right="0" onClick={slideNext}>
                                <ArrowIcon direction="right" width="16px" height="14px" />
                            </NavigationButton>
                        </>
                    )}
                />

                <TouchableSlider {...sliderProps} className="cursor" isInfinite onTap={isImage && onImageClick}>
                    {medias.map((media, index) => (
                        <Slide key={`media-${media.title}`}>
                            <GalleryCard media={media} isLazyLoading={index !== 0} />
                        </Slide>
                    ))}
                </TouchableSlider>
            </MediaWrapper>
        </>
    ) : (
        <MediaWrapper className={image && "zoomable"}>
            <Slide onClick={image ? onImageClick : undefined}>
                <GalleryCard media={medias[0]} isLazyLoading={false} />
            </Slide>
        </MediaWrapper>
    );
};

MediaGallery.propTypes = {
    image: PropTypes.string,
    medias: PropTypes.array,
    sliderProps: PropTypes.object.isRequired,
    sliderOptions: PropTypes.object.isRequired,
};

MediaGallery.defaultProps = {
    image: PLACEHOLDER,
    medias: [],
};

export default MediaGallery;
